import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import MealCard from "../components/MealCard";
import { ListChecks } from "lucide-react";

const TABS = [
  { id: "all",     label: "All",      kind: null },
  { id: "meal",    label: "Meals",    kind: "meal" },
  { id: "drink",   label: "Drinks",   kind: "drink" },
  { id: "package", label: "Packaged", kind: "package" },
];

export default function History() {
  const [tab, setTab] = useState("all");
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const kind = TABS.find((t) => t.id === tab)?.kind || undefined;
      setMeals(await api.listMeals(kind));
    } catch {
      toast.error("Couldn't load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const onDelete = async (id) => {
    try {
      await api.deleteMeal(id);
      setMeals((m) => m.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const onLogAgain = async (id) => {
    try {
      const copy = await api.logAgain(id);
      setMeals((m) => [copy, ...m]);
      toast.success("Logged again for today");
    } catch {
      toast.error("Couldn't log again");
    }
  };

  return (
    <div className="px-5 pt-8 pb-6 space-y-5" data-testid="history-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-sage">History</p>
        <h1 className="font-heading text-3xl font-extrabold text-charcoal mt-2">Your log</h1>
        <p className="text-slate2 mt-2 text-sm leading-relaxed">
          Everything you&apos;ve logged — meals, drinks and packaged foods.
        </p>
      </header>

      <div className="grid grid-cols-4 gap-1.5 p-1.5 bg-sand rounded-2xl" data-testid="history-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`history-tab-${t.id}`}
            className={`py-2 text-xs font-bold tap-shrink rounded-xl transition-colors ${
              tab === t.id ? "bg-charcoal text-cream" : "text-slate2"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="card-soft h-72 animate-pulse bg-sand/50" />
          ))}
        </div>
      ) : meals.length === 0 ? (
        <div className="card-soft p-8 text-center" data-testid="history-empty">
          <div className="w-14 h-14 rounded-2xl bg-sage-light flex items-center justify-center mx-auto">
            <ListChecks className="text-sage" />
          </div>
          <h3 className="font-heading text-lg font-bold text-charcoal mt-4">Nothing yet here</h3>
          <p className="text-sm text-slate2 mt-2">Snap your next entry — even a snack or sip counts.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {meals.map((m) => (
            <MealCard key={m.id} meal={m} onDelete={onDelete} onLogAgain={onLogAgain} />
          ))}
        </div>
      )}
    </div>
  );
}
